import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import apiService from 'common/services/api/apiService';
import ActionStatus from 'common/types/ActionStatus';
import FetchStatus from 'common/types/FetchStatus';
import { AppThunk } from 'configureStore';
import initial from 'domain/initial';
import ChsIntermediary from 'domain/types/ChsIntermediary';
import DraftApplication from '../../domain/types/DraftApplication';
import DraftApplicationRequest from './types/DraftApplicationRequest';
import DraftApplicationResponse from './types/DraftApplicationResponse';

export interface IDraftApplicationSlice {
  meta: {
    fetchStatus: FetchStatus;
    fetchMessage: string | null;
    actionStatus: ActionStatus;
  };
  data: DraftApplication;
}

export const initialState: IDraftApplicationSlice = {
  meta: {
    fetchStatus: FetchStatus.none,
    fetchMessage: null,
    actionStatus: ActionStatus.none,
  },
  data: initial.application(),
};

const DraftApplicationSlice = createSlice({
  name: 'DraftApplication',
  initialState,
  // These are your functions used to update the state.
  // The value returned from each function will be the new state of this slice
  reducers: {
    fetchStart(state) {
      state.meta.fetchStatus = FetchStatus.loading;
      return state;
    },
    fetchFail(state, action: PayloadAction<{ message: string }>) {
      state.meta.fetchStatus = FetchStatus.loaded;
      state.meta.fetchMessage = action.payload.message;
      return state;
    },
    fetchComplete(state, action: PayloadAction<DraftApplication>) {
      state.meta.fetchStatus = FetchStatus.loaded;
      state.data = action.payload;
      return state;
    },
    saveStart(state) {
      state.meta.actionStatus = ActionStatus.saving;
      return state;
    },
    saveComplete(state, action: PayloadAction<DraftApplication>) {
      state.meta.actionStatus = ActionStatus.saved;
      state.data = action.payload;
      return state;
    },
    fetchCompleteAppNotFound() {
      return {
        ...initialState,
        meta: { ...initialState.meta, fetchStatus: FetchStatus.loaded },
      };
    },
    clearApplication() {
      return initialState;
    },
  },
});

export function getDraft(): AppThunk<Promise<DraftApplication | null>> {
  return async function (dispatch, getState) {
    dispatch(DraftApplicationSlice.actions.fetchStart());
    const { access_token } = getState().auth;
    let error;
    try {
      const res = await apiService.get('/drafts', access_token);
      if (res.status === 200) {
        // Due to the fact that this endpoint returns null if no drafts found,
        // we need to call res.text() before converting to json.
        const text = await res.text();
        if (text) {
          const app = JSON.parse(text);
          dispatch(DraftApplicationSlice.actions.fetchComplete(app));
          return Promise.resolve(app);
        } else {
          dispatch(DraftApplicationSlice.actions.fetchCompleteAppNotFound());
          return Promise.resolve(null);
        }
      } else if (res.status === 401) {
        // TODO log out
      } else if (res.status === 400) {
        error = await res.text();
      }
    } catch (e) {
      error = (e instanceof Error && e.message.toString()) || '';
    }
    dispatch(DraftApplicationSlice.actions.fetchFail({ message: error || '' }));
    return Promise.reject(error);
  };
}

export function createDraft(): AppThunk<Promise<DraftApplication>> {
  return async function (dispatch, getState) {
    dispatch(DraftApplicationSlice.actions.fetchStart());
    const { access_token } = getState().auth;
    let error;
    try {
      const res = await apiService.post('/drafts', access_token, {});
      if (res.status === 200) {
        const app = (await res.json()) as DraftApplication;
        dispatch(DraftApplicationSlice.actions.fetchComplete(app));
        return Promise.resolve(app);
      } else if (res.status === 401) {
        // TODO log out
      } else if (res.status === 400) {
        error = await res.text();
      }
    } catch (e) {
      error = (e instanceof Error && e.message.toString()) || '';
    }
    dispatch(DraftApplicationSlice.actions.fetchFail({ message: error || '' }));
    return Promise.reject(error);
  };
}

export function resetDraft(): AppThunk<Promise<DraftApplication>> {
  return async function (dispatch, getState) {
    dispatch(DraftApplicationSlice.actions.fetchStart());
    const { access_token } = getState().auth;
    let error;
    try {
      const res = await apiService.post('/drafts/reset', access_token, {});
      if (res.status === 200) {
        const app = (await res.json()) as DraftApplication;
        dispatch(DraftApplicationSlice.actions.fetchComplete(app));
        return Promise.resolve(app);
      } else if (res.status === 401) {
        // TODO log out
      } else if (res.status === 400) {
        error = await res.text();
      }
    } catch (e) {
      error = (e instanceof Error && e.message.toString()) || '';
    }
    dispatch(DraftApplicationSlice.actions.fetchFail({ message: error || '' }));
    return Promise.reject(error);
  };
}

export function saveDraftApplication(
  draft: DraftApplication
): AppThunk<Promise<void>> {
  return async function (dispatch, getState) {
    const {
      auth: { access_token },
      draftApplication: {
        data: { groupRefKey },
      },
    } = getState();

    const endpoint = `/drafts`;
    let error;

    if (groupRefKey === null) {
      return Promise.reject('Group Reference Key must not be null.');
    }

    try {
      const intermediaries: Array<ChsIntermediary> = [];
      for (const i of draft.intermediaries) {
        if (!i) {
          continue;
        }
        intermediaries.push({
          cd: i.cd,
          roomName: null,
        } as ChsIntermediary);
      }

      const res = await apiService.put<DraftApplicationRequest>(
        endpoint,
        access_token,
        {
          groupRefKey,
          howManyRooms: draft.howManyRooms,
          email: draft.email,
          primaryResIndicator: draft.primaryResIndicator,
          numberOfRooms: draft.numberOfRooms,
          dwellingTypeCd: draft.dwellingTypeCd,
          propertyRelaCd: draft.propertyRelaCd,
          address: draft.address,
          intermediaries,
        }
      );
      if (res.status === 200) {
        const app = (await res.json()) as DraftApplicationResponse;
        dispatch(DraftApplicationSlice.actions.saveComplete(app));
        return Promise.resolve();
      } else if (res.status === 401) {
        // TODO log out
      } else if (res.status === 400) {
        error = await res.text();
      }
    } catch (e) {
      error = (e instanceof Error && e.message.toString()) || '';
    }

    return Promise.reject(error);
  };
}

// Validates and submits a draft application.
// If successful, clears draft from redux.
export function submitDraftApplication(): AppThunk<
  Promise<{ errors: string[] | null; ok: boolean }>
> {
  return async function (dispatch, getState) {
    const {
      auth: { access_token },
      draftApplication,
    } = getState();
    const errors = [];
    try {
      const res = await apiService.post(
        `/drafts/${draftApplication.data.groupRefKey}/submit`,
        access_token,
        {}
      );
      const json = await res.json();
      if (res.ok) {
        dispatch(DraftApplicationSlice.actions.clearApplication());
        return Promise.resolve({ errors: null, ok: true });
      } else if (Array.isArray(json)) {
        // TODO get field validation errors
        errors.push(...json.map((e) => e.message));
      }
    } catch (e) {
      errors.push((e instanceof Error && e.message.toString()) || '');
    }

    return Promise.resolve({ errors, ok: false });
  };
}

export function clearAllDrafts(): AppThunk<Promise<void>> {
  return async function (dispatch, getState) {
    const {
      auth: { access_token },
    } = getState();
    const res = await apiService.post(`/drafts/clear`, access_token, {});
    await res.json();
    if (res.ok) {
      dispatch(DraftApplicationSlice.actions.clearApplication());
      return Promise.resolve();
    }
    return Promise.reject('Unable to clear drafts');
  };
}

export default DraftApplicationSlice.reducer;
